import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Home,
  MenuBook,
  Hub,
  Bolt,
  QuestionAnswer,
  People,
  Person,
  AdminPanelSettings,
  ChevronLeft,
  ChevronRight,
  Explore,
} from '@mui/icons-material';
import { mvkgTokens, readerTokens as R, shellTokens as SH } from '../../theme/mvkgTokens';
import { useAuth } from '../../contexts/AuthContext';
import { useCollections } from '../../contexts/CollectionContext';
import Logo from '../ui/Logo';
import CollectionSwitcher from './CollectionSwitcher';

export const SIDEBAR_WIDTH = SH.sidebarWidth;
export const SIDEBAR_COLLAPSED = SH.sidebarCollapsed;

// Entity-hub pages belong to the section they were reached from: a person's
// page to People, every other vertex (method, dataset, survey...) to Graph.
const isGraphEntity = (p) => (p.startsWith('/entity/') && !p.startsWith('/entity/author'))
  || ['/method/', '/dataset/', '/problem/', '/topic/', '/direction/'].some((x) => p.startsWith(x));

// IA restructure 2026-05-26: surface Workspace as Home, group nav by intent
// (Workspace · Explore · Converse · People · Account). Each item is the same
// shape as before so renderNavButton keeps working unchanged.
const NAV_SECTIONS = [
  {
    label: null, // Home and Library need no heading
    items: [
      // Home now resolves to Workspace (App.js mounts <Workspace /> at "/");
      // matches "/" exactly and the /workspace alias so the active state holds
      // when users land via either URL.
      { path: '/', label: 'Home', icon: Home, match: (p) => p === '/' || p.startsWith('/workspace') },
      { path: '/articles', label: 'Library', icon: MenuBook, match: (p) => p.startsWith('/articles') || p.startsWith('/digest') },
    ],
  },
  {
    label: 'Explore',
    items: [
      { path: '/discovery', label: 'Discovery', icon: Bolt, match: (p) => p.startsWith('/discovery') },
      { path: '/graph', label: 'Graph', icon: Hub, match: (p) => p.startsWith('/graph') || isGraphEntity(p) },
      { path: '/directions', label: 'Directions', icon: Explore, match: (p) => p.startsWith('/directions') },
    ],
  },
  {
    label: null, // Ask and People: a heading would only repeat them
    items: [
      { path: '/rag', label: 'Ask', icon: QuestionAnswer, match: (p) => p.startsWith('/rag') },
      // Co-readers and Authors are now two tabs of a single People page, so the
      // rail collapses to one entry. The tab is selected in-page (?tab=…) and
      // deep-links still work; the rail just needs to light up for any sub-view.
      {
        path: '/author-insights',
        label: 'People',
        icon: People,
        match: (p) => p.startsWith('/author-insights') || p.startsWith('/entity/author'),
      },
    ],
  },
  {
    label: null,
    items: [
      { path: '/profile', label: 'Profile', icon: Person, match: (p) => p === '/profile' },
    ],
  },
];

export default function AppSidebar({ collapsed, onToggleCollapsed }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const { collections } = useCollections();
  const width = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  const renderNavButton = (item) => {
    // Newer matchers accept (pathname, searchParams) so People sub-items can
    // distinguish ?tab=coreaders vs ?tab=authors; older ones still work.
    const active = item.match(location.pathname, searchParams);
    const Icon = item.icon;
    return (
      <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
        <Box
          component={RouterLink}
          to={item.path}
          // Real anchor: ctrl/⌘-click opens in a new tab, hover shows the URL,
          // browser back/forward + "Copy link" all work as expected.
          aria-current={active ? 'page' : undefined}
          sx={{
            width: '100%',
            height: SH.nav.height,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: collapsed ? 0 : 1.25,
            borderRadius: `${SH.nav.radius}px`,
            cursor: 'pointer',
            textDecoration: 'none',
            bgcolor: active ? SH.nav.activeBg : 'transparent',
            color: active ? SH.nav.activeText : SH.nav.text,
            justifyContent: collapsed ? 'center' : 'flex-start',
            transition: 'background-color .15s ease, color .15s ease',
            '& svg': { color: active ? SH.nav.activeIcon : SH.nav.icon, transition: 'color .15s ease' },
            '&:hover': { bgcolor: active ? SH.nav.activeBg : SH.nav.hover, color: SH.nav.activeText },
            '&:focus-visible': { outline: `2px solid ${mvkgTokens.brand.main}`, outlineOffset: 1 },
          }}
        >
          <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
          {!collapsed && (
            <>
              <Typography sx={{ fontSize: R.size.body, fontWeight: active ? 600 : 500, flex: 1, textAlign: 'left', color: 'inherit' }}>
                {item.label}
              </Typography>
              {item.badge && !active && (
                <Chip label={item.badge} size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
            </>
          )}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      component="aside"
      sx={{
        // The column itself carries the white and the rule and stretches with
        // the page (the Layout row is a flex row), so the rail never ends
        // mid-page; only its content is sticky to the viewport.
        width,
        flexShrink: 0,
        alignSelf: 'stretch',
        minHeight: '100vh',
        bgcolor: SH.surface,
        borderRight: `1px solid ${SH.rule}`,
        zIndex: 20,
        transition: 'width 0.25s ease',
      }}
    >
      <Box
        sx={{
          // Flat white on a hairline, like the reader pages: no floating glass
          // card, no radius, no shadow.
          position: 'sticky',
          top: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          px: collapsed ? 1 : 1.5,
          py: 1.5,
          overflowY: 'auto',
        }}
      >
        {/* Brand block — when collapsed, gets the same vertical rhythm as
            an expanded Logo so the icon doesn't hang against the rail top. */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 0.75,
          py: 0.5,
          mb: 1,
        }}>
          {collapsed ? (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: `${R.radius.sm}px`,
                background: mvkgTokens.gradient.brand,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: mvkgTokens.shadow.brandSm,
              }}
            >
              <Hub />
            </Box>
          ) : (
            <Logo size="md" tagline />
          )}
        </Box>

        {!collapsed && collections?.length > 0 && (
          <Box sx={{ mt: 1.5, mb: 1.5, px: 0.25 }}>
            <GroupLabel sx={{ mb: 0.5 }}>Collection</GroupLabel>
            <CollectionSwitcher />
          </Box>
        )}

        <Box component="nav" aria-label="Main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
          {NAV_SECTIONS.map((section, idx) => (
            <React.Fragment key={section.items[0].path}>
              {/* Groups are separated by a hairline; only a group that needs a
                  name gets one (small sentence case, not a mono overline). */}
              {idx > 0 && <Box aria-hidden sx={{ my: 1, mx: collapsed ? 0.5 : 1.25, borderTop: `1px solid ${SH.rule}` }} />}
              {section.label && !collapsed && <GroupLabel sx={{ px: 1.25, mb: 0.25 }}>{section.label}</GroupLabel>}
              {section.items.map(renderNavButton)}
            </React.Fragment>
          ))}
          {/* Admin is gated on user.is_admin and lives at the bottom of Account.
              Kept inline (not in NAV_SECTIONS) so non-admin users don't get an
              empty "Account" section split. */}
          {typeof isAdmin === 'function' && isAdmin() && renderNavButton({
            path: '/admin',
            label: 'Admin',
            icon: AdminPanelSettings,
            match: (p) => p.startsWith('/admin'),
          })}
        </Box>

        {!collapsed && <SidebarTip />}

        <IconButton
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          sx={{
            mt: 1,
            alignSelf: collapsed ? 'center' : 'flex-start',
            width: 32, height: 32,
            borderRadius: `${SH.nav.radius}px`,
            color: SH.nav.icon,
            '&:hover': { bgcolor: SH.nav.hover, color: SH.nav.activeText },
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
    </Box>
  );
}

/** A group's name in the rail: 12/600 sentence case, faint - not a mono overline. */
function GroupLabel({ children, sx }) {
  return (
    <Typography sx={{ fontSize: SH.label.size, fontWeight: SH.label.weight, color: SH.label.color, lineHeight: 1.6, ...sx }}>
      {children}
    </Typography>
  );
}

/**
 * Real, data-driven sidebar tip. Pulls article stats and surfaces the most
 * actionable next step, instead of a hardcoded "Process a paper today…".
 * Hides itself entirely when there's nothing useful to nudge about.
 */
function SidebarTip() {
  const [tip, setTip] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/articles/stats');
        if (cancelled) return;
        const failed = Number(data?.failed || 0);
        const processing = Number(data?.processing || 0);
        const pending = Number(data?.pending || 0);
        const queued = Number(data?.queued || 0);
        const completed = Number(data?.completed || 0);
        const total = Number(data?.total || 0);

        // Pick the most actionable signal — error > queue activity > new corpus.
        if (failed > 0) {
          setTip({
            label: `${failed} failed import${failed > 1 ? 's' : ''}`,
            body: 'Open Library to retry — the pipeline left them stalled.',
            href: '/articles?filter=failed',
            cta: 'Open Library',
          });
        } else if (processing > 0) {
          setTip({
            label: `${processing} paper${processing > 1 ? 's' : ''} processing`,
            body: 'You can keep working; the graph will update when chunks finish.',
            href: '/articles?filter=processing',
            cta: 'See progress',
          });
        } else if ((pending + queued) > 0) {
          const n = pending + queued;
          setTip({
            label: `${n} paper${n > 1 ? 's' : ''} waiting`,
            body: 'Press "Start Processing" to extract — workers are idle.',
            href: '/articles?filter=pending',
            cta: 'Start',
          });
        } else if (total === 0) {
          setTip({
            label: 'No papers yet',
            body: 'Drop a PDF or paste a DOI to seed your knowledge graph.',
            href: '/articles',
            cta: 'Upload',
          });
        } else if (completed > 0) {
          // Healthy: surface a real navigation aid instead of a fake nudge.
          setTip({
            label: `${completed} paper${completed > 1 ? 's' : ''} ready`,
            body: 'Ask the agent or browse the graph — your corpus is current.',
            href: '/rag',
            cta: 'Ask',
          });
        }
      } catch (_) {
        // Silent: better no tip than a misleading one.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!tip) return null;
  return (
    <Box
      component={RouterLink}
      to={tip.href}
      // A quiet card, not a gradient banner: the brand gradient belongs to the
      // logo and one primary action (DESIGN.md, One Gradient Rule).
      sx={{
        mt: 2,
        px: 1.5,
        py: 1.25,
        borderRadius: `${R.radius.md}px`,
        border: `1px solid ${SH.rule}`,
        bgcolor: R.wash,
        color: R.body,
        textDecoration: 'none',
        display: 'block',
        transition: 'border-color .15s ease',
        '&:hover': { borderColor: R.ruleStrong },
        '&:hover .tip-cta': { textDecoration: 'underline' },
        '&:focus-visible': { outline: `2px solid ${mvkgTokens.brand.main}`, outlineOffset: 2 },
      }}
    >
      <Typography sx={{ fontSize: R.size.ui, fontWeight: 600, color: R.ink, lineHeight: 1.4 }}>{tip.label}</Typography>
      <Typography sx={{ mt: 0.25, fontSize: R.size.meta, lineHeight: 1.45, color: R.muted }}>
        {tip.body}
      </Typography>
      <Typography className="tip-cta" component="span" sx={{ mt: 0.75, display: 'inline-block', fontSize: R.size.meta, fontWeight: 600, color: mvkgTokens.brand.main, textUnderlineOffset: '3px' }}>
        {tip.cta}
      </Typography>
    </Box>
  );
}
